import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { postTransferOrder } from 'services/transferOrders/api';
import {
  TransferOrderFormData,
  CreateTransferOrderRequest,
  TransferOrder,
} from 'services/transferOrders/types';
import { activeUserHasPermission } from 'services/user/redux';
import { PermissionType } from 'services/permissions';
import { logErrorCtx } from 'app/logging';

interface UseTransferOrderActionsProps {
  formData: TransferOrderFormData;
  onSuccess: (
    message: string,
    transferOrder?: TransferOrder,
    shouldClose?: boolean
  ) => void;
  onError: (message: string) => void;
  onClose: () => void;
  validateForm?: () => Promise<boolean>;
}

export interface TransferOrderActionHandlers {
  handleSave: () => Promise<void>;
  handleSaveAndClose: () => Promise<void>;
  handleIssue: () => Promise<void>;
  handleQuickFulfill: () => Promise<void>;
  handleDelete: () => Promise<void>;
  isProcessing: boolean;
  canCreate: boolean;
  canDelete: boolean;
}

export const useTransferOrderActions = ({
  formData,
  onSuccess,
  onError,
  onClose,
  validateForm,
}: UseTransferOrderActionsProps): TransferOrderActionHandlers => {
  const [isProcessing, setIsProcessing] = useState(false);

  // Permission checks
  const canCreate = useSelector(
    activeUserHasPermission([PermissionType.TransferOrdersCreate])
  );

  const canDelete = useSelector(
    activeUserHasPermission([PermissionType.TransferOrdersDelete])
  );

  const validateFormData = useCallback((): string | null => {
    if (!formData.sourceLocationId) {
      return 'Source location is required';
    }

    if (!formData.destinationLocationId) {
      return 'Destination location is required';
    }

    if (formData.sourceLocationId === formData.destinationLocationId) {
      return 'Source and destination cannot be the same location';
    }

    // Items are not required for saving a draft transfer order
    // Only validate items if they exist
    if (formData.items && formData.items.length > 0) {
      // Validate each item
      for (const item of formData.items) {
        if (!item.isDeleted) {
          if (item.itemId == null) {
            return 'All items must be selected';
          }
          if (item.quantity == null || item.quantity <= 0) {
            return 'All items must have a positive quantity';
          }
          if (item.accountingClassId == null) {
            return 'All items must have an accounting class selected';
          }
        }
      }
    }

    return null;
  }, [formData]);

  const buildCreateRequest = useCallback((): CreateTransferOrderRequest => {
    return {
      sourceLocationId: formData.sourceLocationId!,
      destinationLocationId: formData.destinationLocationId!,
      transferReason: formData.transferReason?.trim() || undefined,
      items: (formData.items || [])
        .filter(
          (item) =>
            !item.isDeleted &&
            item.itemId != null &&
            item.quantity != null &&
            item.quantity > 0 &&
            item.accountingClassId != null
        )
        .map((item) => ({
          itemId: item.itemId!,
          quantity: item.quantity!,
          accountingClassId: item.accountingClassId!,
          uomId: item.uomId ?? undefined,
          notes: item.notes?.trim() || undefined,
        })),
    };
  }, [formData]);

  const handleSave = useCallback(async () => {
    if (!canCreate) {
      onError('You do not have permission to create transfer orders');
      return;
    }

    // Use provided validation function if available, otherwise use fallback
    if (validateForm) {
      const isValid = await validateForm();
      if (!isValid) {
        return; // Validation errors are handled by the validation function
      }
    } else {
      const validationError = validateFormData();
      if (validationError) {
        onError(validationError);
        return;
      }
    }

    setIsProcessing(true);

    try {
      const request = buildCreateRequest();
      const transferOrder = await postTransferOrder(request);

      onSuccess(
        `Transfer order ${transferOrder.number} created successfully`,
        transferOrder
      );
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to save transfer order', {
        stackTrace: err.stack,
        error: err,
        description: 'Transfer order save operation failed',
        component: 'useTransferOrderActions.handleSave',
      });
      onError('Failed to save transfer order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canCreate,
    validateForm,
    validateFormData,
    buildCreateRequest,
    onSuccess,
    onError,
  ]);

  const handleSaveAndClose = useCallback(async () => {
    if (!canCreate) {
      onError('You do not have permission to create transfer orders');
      return;
    }

    // Use provided validation function if available, otherwise use fallback
    if (validateForm) {
      const isValid = await validateForm();
      if (!isValid) {
        return; // Validation errors are handled by the validation function
      }
    } else {
      const validationError = validateFormData();
      if (validationError) {
        onError(validationError);
        return;
      }
    }

    setIsProcessing(true);

    try {
      const request = buildCreateRequest();
      const transferOrder = await postTransferOrder(request);

      onSuccess(
        `Transfer order ${transferOrder.number} created successfully`,
        transferOrder,
        true // shouldClose = true for Save and Close
      );
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to save and close transfer order', {
        stackTrace: err.stack,
        error: err,
        description: 'Transfer order save and close operation failed',
        component: 'useTransferOrderActions.handleSaveAndClose',
      });
      onError('Failed to save transfer order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canCreate,
    validateFormData,
    buildCreateRequest,
    onSuccess,
    onError,
    onClose,
  ]);

  const handleIssue = useCallback(async () => {
    if (!canCreate) {
      onError('You do not have permission to issue transfer orders');
      return;
    }

    // Use provided validation function if available, otherwise use fallback
    if (validateForm) {
      const isValid = await validateForm();
      if (!isValid) {
        return; // Validation errors are handled by the validation function
      }
    } else {
      const validationError = validateFormData();
      if (validationError) {
        onError(validationError);
        return;
      }
    }

    setIsProcessing(true);

    try {
      const request = buildCreateRequest();
      const transferOrder = await postTransferOrder(request);

      // Issue API will be implemented in future iteration
      // await issueTransferOrder(transferOrder.id);

      onSuccess(
        `Transfer order ${transferOrder.number} issued successfully`,
        transferOrder
      );
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to issue transfer order', {
        stackTrace: err.stack,
        error: err,
        description: 'Transfer order issue operation failed',
        component: 'useTransferOrderActions.handleIssue',
      });
      onError('Failed to issue transfer order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canCreate,
    validateForm,
    validateFormData,
    buildCreateRequest,
    onSuccess,
    onError,
  ]);

  const handleQuickFulfill = useCallback(async () => {
    if (!canCreate) {
      onError('You do not have permission to fulfill transfer orders');
      return;
    }

    // Use provided validation function if available, otherwise use fallback
    if (validateForm) {
      const isValid = await validateForm();
      if (!isValid) {
        return; // Validation errors are handled by the validation function
      }
    } else {
      const validationError = validateFormData();
      if (validationError) {
        onError(validationError);
        return;
      }
    }

    setIsProcessing(true);

    try {
      const request = buildCreateRequest();
      const transferOrder = await postTransferOrder(request);

      // Quick fulfill API will be implemented in future iteration
      // await quickFulfillTransferOrder(transferOrder.id);

      onSuccess(
        `Transfer order ${transferOrder.number} fulfilled successfully`,
        transferOrder
      );
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to fulfill transfer order', {
        stackTrace: err.stack,
        error: err,
        description: 'Transfer order quick fulfill operation failed',
        component: 'useTransferOrderActions.handleQuickFulfill',
      });
      onError('Failed to fulfill transfer order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [
    canCreate,
    validateForm,
    validateFormData,
    buildCreateRequest,
    onSuccess,
    onError,
  ]);

  const handleDelete = useCallback(async () => {
    if (!canDelete) {
      onError('You do not have permission to delete transfer orders');
      return;
    }

    // Only allow delete for new/unsaved transfer orders
    if (formData.isEditing) {
      onError('Cannot delete saved transfer orders from this form');
      return;
    }

    setIsProcessing(true);

    try {
      // For new transfer orders, just close the form
      onSuccess('Transfer order discarded');
      onClose();
    } catch (error) {
      const err = error as Error;
      logErrorCtx('Failed to delete transfer order', {
        stackTrace: err.stack,
        error: err,
        description: 'Transfer order delete operation failed',
        component: 'useTransferOrderActions.handleDelete',
      });
      onError('Failed to delete transfer order. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  }, [canDelete, formData.isEditing, onSuccess, onError, onClose]);

  return {
    handleSave,
    handleSaveAndClose,
    handleIssue,
    handleQuickFulfill,
    handleDelete,
    isProcessing,
    canCreate,
    canDelete,
  };
};
